import { type JSX, useEffect } from "react";

import { Box, Checkbox, FormControlLabel } from "@mui/material";
import {
  CommonErrorBoundary,
  OAuthManager,
} from "@plansoft-configuration-renderer/components";
import {
  GatewayDest,
  GatewayProvider,
} from "@plansoft-configuration-renderer/gateway";
import { LayoutPreselectContext } from "@plansoft-configuration-renderer/layout";

import { isDevEnvironment, isInIframe, language } from "@/Router";

import { CFGObjectSelect } from "./CFGObjectSelect";
import { MinimalAppContext } from "./MinimalAppContext";
import { MinimalRenderer } from "./MinimalRenderer";
import { useConfigurationFinishedContext } from "./controls/ConfigurationFinishedPage/ConfigurationFinishedContext";
import ConfigurationFinishedPage from "./controls/ConfigurationFinishedPage/ConfigurationFinishedPage";
import { useIsFastConfigurationContext } from "./controls/IsFastConfigurationContext/IsFastConfigurationContext";

export const isDevOrStagEnvironment =
  window.location.href.includes("dev") ||
  window.location.href.includes("stag") ||
  window.location.href.includes("localhost");

const queryString = window.location.search;
const urlParams = new URLSearchParams(queryString);

const cfgObjectId = urlParams.get("cfgObjectId");
const cfgSessionId = urlParams.get("cfgSessionId");
const isFastConfigurationURL = urlParams.get("schnellkonfigurator");
const isProfiKonfigurator = cfgObjectId === "CFG_PROFI_KONFIGURATOR";

if (isDevOrStagEnvironment) {
  console.log({ cfgObjectId, cfgSessionId });
}

export function MinimalApp(): JSX.Element {
  const { isConfigurationFinished } = useConfigurationFinishedContext();
  const { isFastConfiguration, setIsFastConfiguration } =
    useIsFastConfigurationContext();

  const fastConfiguration = window.sessionStorage.getItem("fastConfiguration");

  useEffect(() => {
    if (fastConfiguration) {
      setIsFastConfiguration(fastConfiguration === "true");
    }
  }, [fastConfiguration, setIsFastConfiguration]);

  const isFastConfigurationEffective =
    isFastConfiguration || isFastConfigurationURL === "true";

  const mapping = {
    CFG_PROFI_KONFIGURATOR: isFastConfigurationEffective
      ? "LAYOUT_PROFI_KONFIGURATOR_SCHNELL"
      : "LAYOUT_PROFI_KONFIGURATOR",
  };

  function handleFastConfigurationChange(
    event: React.ChangeEvent<HTMLInputElement>,
  ): void {
    setIsFastConfiguration(event.target.checked);
    window.sessionStorage.setItem(
      "fastConfiguration",
      event.target.checked.toString(),
    );
  }

  return isConfigurationFinished ? (
    <ConfigurationFinishedPage />
  ) : (
    <MinimalAppContext>
      <LayoutPreselectContext.Provider value={mapping}>
        <GatewayProvider>
          <CommonErrorBoundary>
            {cfgObjectId || cfgSessionId ? (
              <>
                {!isInIframe &&
                  isDevEnvironment &&
                  isProfiKonfigurator &&
                  fastConfiguration && (
                    <FormControlLabel
                      sx={{ marginLeft: "1rem" }}
                      label="Schnellkonfiguration"
                      control={
                        <Checkbox
                          checked={isFastConfiguration}
                          onChange={handleFastConfigurationChange}
                        />
                      }
                    />
                  )}

                <MinimalRenderer
                  baseUrl={import.meta.env.VITE_CONFIGURATOR_BASE_URL}
                  cfgObjectId={cfgObjectId ? cfgObjectId : ""}
                  sessionId={cfgSessionId ? cfgSessionId : ""}
                  language={language ? language : "de-DE"}
                />
              </>
            ) : (
              <CFGObjectSelect />
            )}
          </CommonErrorBoundary>
          <OAuthManager
            basePath={import.meta.env.VITE_CONFIGURATOR_BASE_URL}
            isLocalDevelopment={import.meta.env.DEV}
          />

          <Box position="sticky" top={0}>
            <GatewayDest name="navigation" />
          </Box>
        </GatewayProvider>
      </LayoutPreselectContext.Provider>
    </MinimalAppContext>
  );
}
